import React, { useState, useEffect } from 'react';
import { Heart, Info, ChevronDown, ChevronUp, Plus, X, BookOpen, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * PersonalAnchorSection - Section 4: Personal Connection
 *
 * Fields:
 * - Micro-moment from your life (textarea, 220 chars)
 * - Core motivations (array, 2-3 items, 60 chars each)
 *
 * Features:
 * - Essay Interview diversion if micro_moment is empty
 * - Links to Character & Identity, Moments that Matter, Values & Beliefs interviews
 */
export default function PersonalAnchorSection({ data, onChange, collegeData, essayId, userId }) {
  const [showGuidance, setShowGuidance] = useState(false);
  const [showDiversionModal, setShowDiversionModal] = useState(false);

  // Check if we should show the diversion modal
  useEffect(() => {
    const shouldShowDiversion = (
      !data.micro_moment &&
      !data.diversion_dismissed &&
      !data.motivations?.length
    );

    if (shouldShowDiversion) {
      // Show modal after a brief delay so user sees the section first
      const timer = setTimeout(() => {
        setShowDiversionModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // === MOTIVATIONS ===
  const addMotivation = () => {
    const motivations = data.motivations || [];
    if (motivations.length >= 3) {
      toast.error('Maximum 3 motivations allowed');
      return;
    }
    onChange({
      ...data,
      motivations: [...motivations, '']
    });
  };

  const updateMotivation = (index, value) => {
    const motivations = [...(data.motivations || [])];
    motivations[index] = value;
    onChange({ ...data, motivations });
  };

  const removeMotivation = (index) => {
    const motivations = [...(data.motivations || [])];
    motivations.splice(index, 1);
    onChange({ ...data, motivations });
  };

  const dismissDiversion = () => {
    setShowDiversionModal(false);
    onChange({ ...data, diversion_dismissed: true });
    toast.success('You can access essay interviews anytime from the panel on the left');
  };

  return (
    <div className="space-y-8">
      {/* Essay Diversion Modal */}
      {showDiversionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start space-x-3">
                <BookOpen className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Need Help Finding Your Story?</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    The best "Why School" essays include a personal moment that grounds your interest.
                    Our essay interviews can help you discover these stories.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700 font-medium">
                ✓ Your progress here is automatically saved
              </p>

              {/* Interview Cards */}
              <div className="space-y-3">
                <InterviewCard
                  title="Character & Identity"
                  duration="15-20 min"
                  description="Uncover defining traits and values through guided questions about key moments in your life"
                  href={`/essays/character-identity?return_to=why-school-builder&essay_id=${essayId}`}
                />

                <InterviewCard
                  title="Moments that Matter"
                  duration="10-15 min"
                  description="Identify pivotal experiences that shaped your interests or changed your perspective"
                  href={`/essays/moments-that-matter?return_to=why-school-builder&essay_id=${essayId}`}
                />

                <InterviewCard
                  title="Values & Beliefs"
                  duration="15 min"
                  description="Explore what matters most to you and why, through reflective prompts"
                  href={`/essays/values-beliefs?return_to=why-school-builder&essay_id=${essayId}`}
                />
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">How this works:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Choose an interview above (opens in new tab)</li>
                      <li>Complete the interview questions</li>
                      <li>Return here—your story will be waiting in the left panel</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between">
              <button
                onClick={dismissDiversion}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Skip for now
              </button>
              <button
                onClick={dismissDiversion}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
              >
                I'll write my own story
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Ground your interest in the school with a personal moment or core motivation.
              This prevents the essay from reading like a brochure. Show <em>why you care</em>, not just why the school is great.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Example:</p>
              <p className="italic">
                "When my little sister asked why her hearing aid didn't work underwater, I spent
                weeks trying to waterproof it in our garage. That's when I realized: the best
                engineering asks 'why not?' before 'how?'"
              </p>
            </div>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Weak Example:</p>
              <p className="line-through italic">
                "I've always been passionate about engineering and helping people."
              </p>
              <p className="text-red-700 text-xs mt-1">
                ❌ Too generic—no specific moment, no personal stakes
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Need Help? Button */}
      {data.diversion_dismissed && !data.micro_moment && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-5 h-5 text-amber-700" />
              <span className="text-sm font-medium text-amber-900">
                Stuck? Our essay interviews can help you find your story
              </span>
            </div>
            <button
              onClick={() => setShowDiversionModal(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition text-sm font-medium"
            >
              Show Interviews
            </button>
          </div>
        </div>
      )}

      {/* Field 1: Micro-moment */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          A Micro-Moment from Your Life
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Describe a brief, specific moment that sparked your interest or shaped how you think.
          Focus on sensory details and what you <em>did</em>, not just what you felt.
        </p>
        <textarea
          value={data.micro_moment || ''}
          onChange={(e) => onChange({ ...data, micro_moment: e.target.value })}
          placeholder="Example: &quot;When my sister asked why her hearing aid didn't work underwater, I spent weeks in our garage trying to waterproof it.&quot;"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
          style={{ height: 'auto', minHeight: '100px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Field 2: Core Motivations */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Core Motivations (2-3)
          </label>
          <button
            onClick={addMotivation}
            disabled={(data.motivations || []).length >= 3}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>Add Motivation</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          What drives you? Keep these brief and specific—not "I want to help people" but
          "I want to build adaptive technology for people with disabilities."
        </p>

        {/* Motivations List */}
        <div className="space-y-3">
          {(data.motivations || []).map((motivation, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Motivation #{index + 1}
                  </label>
                  <input
                    type="text"
                    value={motivation || ''}
                    onChange={(e) => updateMotivation(index, e.target.value)}
                    placeholder="e.g., Building adaptive tech for accessibility"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeMotivation(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {(!data.motivations || data.motivations.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Heart className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-3">No motivations added yet</p>
              <button
                onClick={addMotivation}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                <Plus className="w-4 h-4" />
                <span>Add Your First Motivation</span>
              </button>
            </div>
          )}
        </div>

        {/* Minimum requirement note */}
        {data.motivations && data.motivations.length > 0 && data.motivations.length < 2 && (
          <p className="text-sm text-amber-600 mt-2">
            ⚠ Add at least {2 - data.motivations.length} more motivation(s) to complete this section
          </p>
        )}
      </div>

      {/* Example/Tip Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          Your personal moment doesn't need to be dramatic or life-changing. Small, specific
          moments often work better than big abstract statements. A single afternoon in your
          garage can say more than "I've always loved science."
        </p>
      </div>
    </div>
  );
}

// Helper component for interview cards
function InterviewCard({ title, duration, description, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition group"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h4 className="font-semibold text-gray-900 group-hover:text-indigo-700">
              {title}
            </h4>
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
              {duration}
            </span>
          </div>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transform -rotate-90" />
      </div>
    </a>
  );
}
